import api from "../lib/api";
import { Landmark, ScrollText, HandHeart, Receipt, CalendarCheck, FileCheck2, ShieldCheck, Fingerprint, BadgeCheck } from "lucide-react";

// ---- API (public demo) ----
export const demo = {
  overview: () => api.get("/demo/overview").then((r) => r.data),
  temple: () => api.get("/demo/temple").then((r) => r.data),
  temples: () => api.get("/demo/temples").then((r) => r.data),
  sevas: (templeId) => api.get("/demo/sevas", { params: templeId ? { temple_id: templeId } : {} }).then((r) => r.data),
  seva: (id) => api.get(`/demo/sevas/${id}`).then((r) => r.data),
  list: () => api.get("/demo/sankalpas").then((r) => r.data),
  record: (rid) => api.get(`/demo/sankalpas/${rid}`).then((r) => r.data),
  create: (body) => api.post("/demo/sankalpas", body).then((r) => r.data),
  accept: (rid) => api.post(`/demo/sankalpas/${rid}/accept`, {}).then((r) => r.data),
  fulfil: (rid, body) => api.post(`/demo/sankalpas/${rid}/fulfil`, body).then((r) => r.data),
  addEvidence: (rid, formData) => api.post(`/demo/sankalpas/${rid}/evidence`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  submitValidation: (rid) => api.post(`/demo/sankalpas/${rid}/submit-validation`, {}).then((r) => r.data),
  validate: (rid, body) => api.post(`/demo/sankalpas/${rid}/validate`, body).then((r) => r.data),
  tamperPreview: (rid, body) => api.post(`/demo/sankalpas/${rid}/tamper-preview`, body).then((r) => r.data),
  reset: () => api.post("/demo/reset", {}).then((r) => r.data),
};

// ---- status model ----
export const STATE_ORDER = ["BOOKED", "ACCEPTED", "FULFILMENT_RECORDED", "EVIDENCE_RECEIVED", "VALIDATION_PENDING", "VALIDATED", "CERTIFIED"];

export const STATUS_META = {
  BOOKED: { label: "Booked", tone: "slate" },
  ACCEPTED: { label: "Accepted", tone: "slate" },
  FULFILMENT_RECORDED: { label: "Fulfilment Recorded", tone: "amber" },
  EVIDENCE_RECEIVED: { label: "Evidence Received", tone: "amber" },
  VALIDATION_PENDING: { label: "Validation Pending", tone: "amber" },
  VALIDATED: { label: "Validated", tone: "green" },
  CERTIFIED: { label: "Certified", tone: "green" },
  CERTIFICATION_WITHHELD: { label: "Certification Withheld", tone: "withheld" },
};

const TONE = {
  slate: "bg-slate-100 text-slate-600 border-slate-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  withheld: "bg-slate-100 text-slate-700 border-slate-400",
};

export function StatusBadge({ status, className = "" }) {
  const m = STATUS_META[status] || { label: status, tone: "slate" };
  return (
    <span data-testid={`c1-status-${status}`}
      className={`inline-flex items-center border px-2 py-0.5 rounded-sm font-mono text-[10px] uppercase tracking-[0.12em] ${TONE[m.tone]} ${className}`}>
      {m.label}
    </span>
  );
}

export function SyntheticTag({ label = "SYNTHETIC / DEMO", className = "" }) {
  return (
    <span data-testid="synthetic-tag"
      className={`inline-flex items-center gap-1 border border-dashed border-slate-400 text-slate-500 bg-white px-2 py-0.5 rounded-sm font-mono text-[9px] uppercase tracking-[0.18em] ${className}`}>
      {label}
    </span>
  );
}

export function LiveTag({ label = "LIVE · VERIFIED TEMPLE", className = "" }) {
  return (
    <span data-testid="live-tag-devotee"
      className={`inline-flex items-center gap-1 border border-emerald-300 bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded-sm font-mono text-[9px] uppercase tracking-[0.18em] ${className}`}>
      {label}
    </span>
  );
}

export const money = (n, cur = "INR") =>
  `${cur === "INR" ? "₹" : ""}${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const TRUST_CHAIN = [
  { key: "temple", label: "Verified Temple", icon: Landmark },
  { key: "seva", label: "Temple-Approved Seva", icon: ScrollText },
  { key: "sankalpa", label: "Devotee Sankalpa", icon: HandHeart },
  { key: "money", label: "Transparent Money Flow", icon: Receipt },
  { key: "fulfilment", label: "Temple-Side Fulfilment", icon: CalendarCheck },
  { key: "evidence", label: "Evidence", icon: FileCheck2 },
  { key: "validation", label: "Validation", icon: ShieldCheck },
  { key: "integrity", label: "Cryptographic Integrity", icon: Fingerprint },
  { key: "record", label: "Verified Record", icon: BadgeCheck },
];
